import { useNavigate } from 'react-router';
import { useCallback, useEffect } from 'react';
import {
  useAcceptOfferMutation,
  useApplicationProposalsQuery,
  useApplicationQuery,
  useClientOfferQuery,
  useCreateApplicationAndSendGACookiesMutation,
} from 'hooks/webapi';
import useCalculator from 'hooks/calculator/useCalculator';
import { WAITING } from 'Router/routes';
import { getApplicationSource } from 'utils/isInWebView.ts';
import { postMessageToApp } from 'utils/postMessageToApp.ts';

export default function useDownsalePage() {
  const navigate = useNavigate();

  // Queries
  const { data: application } = useApplicationQuery(true);
  const { data: proposalsData } = useApplicationProposalsQuery();
  const { data: clientOfferData } = useClientOfferQuery(
    {
      amount: proposalsData?.bestOfferProposal?.amount,
      term: proposalsData?.bestOfferProposal?.term,
    },
    !!proposalsData?.bestOfferProposal,
  );

  // Mutations
  const {
    mutateAsync: acceptClientOffer,
    isPending: acceptClientOfferIsPending,
  } = useAcceptOfferMutation();
  const {
    mutateAsync: createClientApplication,
    isPending: createClientApplicationIsPending,
  } = useCreateApplicationAndSendGACookiesMutation();

  // Handle calculator values
  const { handleAmountChange } = useCalculator();
  useEffect(() => {
    if (!proposalsData?.bestOfferProposal) return;

    const { amount } = proposalsData.bestOfferProposal;

    handleAmountChange(amount);
  }, [handleAmountChange, proposalsData?.bestOfferProposal]);

  const handleOnSubmit = useCallback(async () => {
    if (!proposalsData?.bestOfferProposal) return;

    const { amount, term } = proposalsData.bestOfferProposal;

    try {
      await createClientApplication({
        amount,
        term,
        type: 'WEB',
        source: getApplicationSource(),
        typeDetail: 'WEB_OFFER',
      });

      await acceptClientOffer({
        amount,
        term,
      });
      postMessageToApp('downsale_accepted', { amount, term });

      navigate(WAITING);
    } catch {
      // Empty catch
    }
  }, [
    proposalsData?.bestOfferProposal,
    createClientApplication,
    acceptClientOffer,
    navigate,
  ]);

  return {
    originalAmount: application?.amount,
    newAmount: proposalsData?.bestOfferProposal?.amount,
    clientOfferData,
    handleOnSubmit,
    isPending: createClientApplicationIsPending || acceptClientOfferIsPending,
  };
}
